import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authService } from '../services/authService';
import { LoginSuccessDto } from '../types/auth';
import { useNavigate, useLocation } from 'react-router-dom';

interface AuthContextType {
  isAuthenticated: boolean;
  user: LoginSuccessDto | null;
  accessToken: string | null;
  login: (username: string, password: string) => Promise<void>;
  loginWithJWT: (token: string) => Promise<void>;
  logout: () => void;
  loading: boolean;
  error: string | null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    // Inicializar com o valor do localStorage
    const savedAuth = localStorage.getItem('tbi_authenticated');
    const savedUser = localStorage.getItem('tbi_user');
    const savedToken = localStorage.getItem('tbi_access_token');
    return !!(savedAuth === 'true' && savedUser && savedToken);
  });
  const [user, setUser] = useState<LoginSuccessDto | null>(() => {
    // Inicializar com o valor do localStorage
    const savedUser = localStorage.getItem('tbi_user');
    if (savedUser) {
      try {
        return JSON.parse(savedUser);
      } catch (e) {
        return null;
      }
    }
    return null;
  });
  const [accessToken, setAccessToken] = useState<string | null>(() => {
    // Inicializar com o valor do localStorage
    return localStorage.getItem('tbi_access_token');
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  const logout = useCallback(() => {
    setUser(null);
    setAccessToken(null);
    setIsAuthenticated(false);
    setError(null);
    
    localStorage.removeItem('tbi_user');
    localStorage.removeItem('tbi_access_token');
    localStorage.removeItem('tbi_refresh_token');
    localStorage.removeItem('tbi_authenticated');
  }, []);

  useEffect(() => {
    // Validar se os dados salvos ainda são válidos
    if (isAuthenticated && (!user || !accessToken)) {
      logout();
    }
  }, [isAuthenticated, user, accessToken, logout]);

  const login = async (username: string, password: string) => {
    try {
      setLoading(true);
      setError(null);

      if (!username.trim() || !password.trim()) {
        setError('Username and password are required');
        return;
      }

      // Validar se é email da Iterpec
      if (username.includes('@') && !username.toLowerCase().endsWith('@iterpec.com')) {
        setError('Only Iterpec employees can access this system. Please use your @iterpec.com email.');
        return;
      }

      const response = await authService.login({
        user: username,
        password: password
      });
      
      const userData = {
        ...response,
        createAt: new Date().toISOString()
      };

      setUser(userData);
      setAccessToken(response.accessToken);
      setIsAuthenticated(true);
      
      localStorage.setItem('tbi_user', JSON.stringify(userData));
      localStorage.setItem('tbi_access_token', response.accessToken);
      localStorage.setItem('tbi_refresh_token', response.refreshToken);
      localStorage.setItem('tbi_authenticated', 'true');
      
    } catch (err: any) {
      console.error('Login error:', err);
      
      if (err.response?.status === 401) {
        setError('Invalid username or password');
      } else if (err.response?.status === 403) {
        if (err.response?.data?.message === "NEW_PASSWORD_REQUIRED") {
          navigate('/update-password');
        } else {
          setError('Password change required. Please contact administrator.');
        }
      } else {
        setError('Invalid username or password');
      }
    } finally {
      setLoading(false);
    }
  };

  const loginWithJWT = async (token: string) => {
    try {
      setLoading(true);
      setError(null);

      const response = await authService.loginWithJWT(token);
      
      const userData = {
        ...response,
        createAt: new Date().toISOString()
      };

      setUser(userData);
      setAccessToken(response.accessToken);
      setIsAuthenticated(true);
      
      localStorage.setItem('tbi_user', JSON.stringify(userData));
      localStorage.setItem('tbi_access_token', response.accessToken);
      localStorage.setItem('tbi_refresh_token', response.refreshToken);
      localStorage.setItem('tbi_authenticated', 'true');
      
    } catch (err: any) {
      console.error('JWT Login error:', err);
      setError('Authentication failed');
    } finally {
      setLoading(false);
    }
  };

  const value = {
    isAuthenticated,
    user,
    accessToken,
    login,
    loginWithJWT,
    logout,
    loading,
    error
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
